import {
  Control,
  Controller,
  FieldErrors,
  UseFormWatch,
} from 'react-hook-form';
import { PolicyAction, PolicyFormValues } from '../policy-dialog';
import z from 'zod';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';

interface RemoveDomainFindableSharesActionProps {
  watch: UseFormWatch<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
  control: Control<PolicyFormValues>;
}

export const RemoveDomainFindableSharesActionSchema = z
  .object({
    type: z.literal('Remove_Domain_Findable_Shares'),
    removeDomainFindableShares: z.boolean(),
    removeSearchableShares: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (!data.removeDomainFindableShares && !data.removeSearchableShares) {
      ctx.addIssue({
        code: 'custom',
        path: ['removeDomainFindableShares'],
        message: 'At least one checkbox must be selected.',
      });
    }
  });

export type RemoveDomainFindableSharesActionType = z.infer<
  typeof RemoveDomainFindableSharesActionSchema
>;

function isRemoveDomainFindableSharesAction(
  action: PolicyAction
): action is RemoveDomainFindableSharesActionType {
  return action.type === 'Remove_Domain_Findable_Shares';
}

export default function RemoveDomainFindableSharesAction({
  watch,
  control,
  errors,
}: RemoveDomainFindableSharesActionProps) {
  const actionData = watch('actionData');

  const idx = actionData
    .map((action) => action.type)
    .indexOf('Remove_Domain_Findable_Shares');
  const actionAtIndex = actionData[idx];
  if (!isRemoveDomainFindableSharesAction(actionAtIndex)) {
    return null;
  }

  const error: FieldErrors<RemoveDomainFindableSharesActionType> | undefined =
    errors?.actionData?.[idx];

  return (
    <Stack sx={{ p: 1 }}>
      <Stack gap={1}>
        <Controller
          name={`actionData.${idx}.removeSearchableShares`}
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={field.value === true}
                  onChange={() => field.onChange(!field.value)}
                />
              }
              sx={{ width: 'fit-content' }}
              label="Can Find in Search Results"
            />
          )}
        />
        <Controller
          name={`actionData.${idx}.removeDomainFindableShares`}
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={field.value === true}
                  onChange={() => field.onChange(!field.value)}
                />
              }
              sx={{ width: 'fit-content' }}
              label="Must Have Link to Access"
            />
          )}
        />
        <FormHelperText error>
          {error?.removeDomainFindableShares?.message || ' '}
        </FormHelperText>
      </Stack>
    </Stack>
  );
}
